import { useState, useEffect } from 'react'
import { ArrowLeft, Phone, Check, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { subscriptionRepo, authRepo, profileRepo } from '@/lib/repositories'
import { toE164, formatPhoneDisplay, isValidPhoneInput } from '@/lib/utils/phoneUtils'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

export function SmsConsentPage() {
  const navigate = useNavigate()
  const [phoneInput, setPhoneInput] = useState('')
  const [consentChecked, setConsentChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        const sub = await subscriptionRepo.getSMSSubscription()
        if (sub && sub.isActive) {
          setIsSubscribed(true)
          setPhoneInput(formatPhoneDisplay(sub.phoneNumber))
        } else {
          // Pre-fill from profile phone if no subscription yet
          const user = await authRepo.getCurrentUser()
          if (user?.username) {
            const profile = await profileRepo.getProfile(user.username)
            if (profile?.phoneNumber) {
              setPhoneInput(formatPhoneDisplay(profile.phoneNumber))
            }
          }
        }
      } catch {
        // Not logged in or no subscription -- that's fine
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])

  const canSubmit = isValidPhoneInput(phoneInput) && consentChecked && !isSubmitting

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11)
    setPhoneInput(formatPhoneDisplay(digits))
  }

  const handleOptIn = async () => {
    if (!canSubmit) return

    try {
      setIsSubmitting(true)
      const cleaned = toE164(phoneInput)
      await subscriptionRepo.subscribeToSMS(cleaned)
      analytics.capture(AnalyticsEvents.SMS_SUBSCRIBED)
      setIsSubscribed(true)
      toast.success('SMS notifications enabled', {
        description: 'You will receive account notifications via text message.',
      })
    } catch (error) {
      console.error('Failed to subscribe:', error)
      toast.error('Failed to enable SMS notifications', {
        description:
          error instanceof Error ? error.message : 'Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOptOut = async () => {
    try {
      setIsSubmitting(true)
      await subscriptionRepo.unsubscribeFromSMS()
      analytics.capture(AnalyticsEvents.SMS_UNSUBSCRIBED)
      setIsSubscribed(false)
      setConsentChecked(false)
      toast.success('SMS notifications disabled', {
        description: 'You will no longer receive text messages from Sovereign.',
      })
    } catch (error) {
      console.error('Failed to unsubscribe:', error)
      toast.error('Failed to disable SMS notifications', {
        description:
          error instanceof Error ? error.message : 'Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-sovereign-mesh">
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50 pointer-events-none" />

      <div className="relative max-w-2xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-neutral-400 hover:text-white mb-6 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <span
              className="text-black text-2xl"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
                fontStyle: 'italic',
                WebkitTextStroke: '1px black',
              }}
            >
              S
            </span>
          </div>
          <div>
            <h1
              className="text-xl font-bold text-white"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
              }}
            >
              Sovereign
            </h1>
            <p className="text-xs text-neutral-500">SMS Notifications</p>
          </div>
        </div>

        {/* Opt-In Form */}
        <div className="bg-neutral-950/60 border border-white/[0.06] rounded-2xl p-6 sm:p-8 space-y-6">
          <h2 className="text-lg font-bold text-white">
            SMS Notifications — Opt In
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-neutral-500 animate-spin" />
            </div>
          ) : isSubscribed ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-emerald-400 font-semibold text-sm">
                      SMS notifications are enabled
                    </p>
                    <p className="text-emerald-400/70 text-sm mt-1">
                      Receiving notifications at {phoneInput}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleOptOut}
                disabled={isSubmitting}
                variant="ghost"
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Unsubscribe from SMS
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-neutral-400">
                Opt in to receive SMS notifications from Sovereign. This is
                completely optional and is not required to use the platform.
              </p>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label
                  htmlFor="sms-phone"
                  className="text-neutral-300 flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="sms-phone"
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
                  disabled={isSubmitting}
                />
              </div>

              {/* Consent Checkbox -- must be unchecked by default per TCPA/CTIA */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="sr-only peer"
                    disabled={isSubmitting}
                  />
                  <div className="w-5 h-5 rounded border-2 border-neutral-600 bg-neutral-900 peer-checked:bg-emerald-500 peer-checked:border-emerald-500 transition-colors flex items-center justify-center">
                    {consentChecked && (
                      <Check className="w-3.5 h-3.5 text-black" />
                    )}
                  </div>
                </div>
                <span className="text-xs text-neutral-400 leading-relaxed">
                  I agree to receive SMS notifications from Sovereign at the
                  phone number provided. Message frequency varies (approx. 1–5
                  msgs/week). Msg &amp; data rates may apply. Reply STOP to opt
                  out. Reply HELP for help. Consent is not required to use
                  Sovereign.{' '}
                  <a
                    href="/terms"
                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>{' '}
                  &amp;{' '}
                  <a
                    href="/privacy"
                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>

              {/* Submit */}
              <Button
                onClick={handleOptIn}
                disabled={!canSubmit}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-black font-semibold text-base disabled:opacity-40 shadow-lg shadow-emerald-500/25"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Subscribing...
                  </span>
                ) : (
                  'Enable SMS Notifications'
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Policy Reference */}
        <div className="bg-neutral-950/60 border border-white/[0.06] rounded-2xl p-6 sm:p-8 space-y-5 mt-6">
          <h3 className="text-sm font-semibold text-white">
            SMS Notification Policy
          </h3>

          <div className="space-y-4 text-sm text-neutral-300 leading-relaxed">
            <section>
              <h4 className="text-white font-semibold mb-1 text-xs uppercase tracking-wide">
                What Messages You Will Receive
              </h4>
              <ul className="list-disc list-inside space-y-1 text-neutral-400 text-xs">
                <li>
                  Notifications when new content is posted on the platform
                </li>
                <li>Account status updates (approval, access changes)</li>
                <li>Important platform announcements</li>
              </ul>
              <p className="mt-1 text-neutral-500 text-xs">
                Message frequency varies. Typically 1–5 messages per week.
              </p>
            </section>

            <section>
              <h4 className="text-white font-semibold mb-1 text-xs uppercase tracking-wide">
                Message &amp; Data Rates
              </h4>
              <p className="text-xs text-neutral-400">
                Message and data rates may apply. Sovereign does not charge for
                SMS messages, but your wireless carrier may apply standard
                messaging fees.
              </p>
            </section>

            <section>
              <h4 className="text-white font-semibold mb-1 text-xs uppercase tracking-wide">
                How to Opt Out
              </h4>
              <p className="text-xs text-neutral-400">
                Reply{' '}
                <span className="text-white font-mono font-semibold bg-neutral-800 px-1 py-0.5 rounded">
                  STOP
                </span>{' '}
                to any message, or use the unsubscribe button above. You will
                receive a confirmation and no further texts will be sent.
              </p>
            </section>

            <section>
              <h4 className="text-white font-semibold mb-1 text-xs uppercase tracking-wide">
                How to Get Help
              </h4>
              <p className="text-xs text-neutral-400">
                Reply{' '}
                <span className="text-white font-mono font-semibold bg-neutral-800 px-1 py-0.5 rounded">
                  HELP
                </span>{' '}
                to any message, or contact{' '}
                <a
                  href="mailto:sovereign.io.app@gmail.com"
                  className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                >
                  sovereign.io.app@gmail.com
                </a>
                .
              </p>
            </section>

            <section>
              <h4 className="text-white font-semibold mb-1 text-xs uppercase tracking-wide">
                Privacy
              </h4>
              <p className="text-xs text-neutral-400">
                Your phone number is stored securely and is never shared with
                third parties for marketing purposes. Phone numbers are used
                solely for delivering Sovereign platform notifications.
              </p>
            </section>
          </div>

          <div className="pt-3 border-t border-white/[0.06] text-xs text-neutral-600 space-y-1">
            <p>Sovereign</p>
            <p>
              Contact:{' '}
              <a
                href="mailto:sovereign.io.app@gmail.com"
                className="text-neutral-500 hover:text-neutral-400 underline underline-offset-2"
              >
                sovereign.io.app@gmail.com
              </a>
            </p>
            <p>Last updated: March 2026</p>
          </div>
        </div>
      </div>
    </div>
  )
}
